import { createFileRoute } from '@tanstack/react-router'
import { FeaturePage } from '@/components/feature-page'
import { ProductFrame } from '@/components/marketing/product-frame'
import { UptimeBarsPiece } from '@/components/marketing/ui-pieces'
import { GRANT_MONTHS, NOT_ELIGIBLE, REGISTER_CREDITS } from '@/lib/nonprofit'
import { tiers } from '@/lib/plans'

const PRO_FEATURES = (tiers.find((t) => t.plan === 'pro')?.features ?? []).filter((f) => f.included).map((f) => f.name)

export const Route = createFileRoute('/_site/for/nonprofits')({
  component: NonprofitsPage,
  head: () => ({
    meta: [
      { title: 'Free Pro for Nonprofits and Charities | Uptrack' },
      { name: 'description', content: `Registered nonprofits and charities get Uptrack Pro free for ${GRANT_MONTHS} months. It starts at once when your email domain is on our lists, or after a short review.` },
      { property: 'og:title', content: 'Free Pro for Nonprofits and Charities | Uptrack' },
      { property: 'og:description', content: `Pro is free for ${GRANT_MONTHS} months for registered nonprofits and charities, and renews while you still qualify.` },
      { property: 'og:image', content: 'https://uptrack.app/og/for-nonprofits.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/nonprofits' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Nonprofits', item: 'https://uptrack.app/for/nonprofits' },
          ],
        }),
      },
    ],
  }),
})

function NonprofitsPage() {
  return (
    <FeaturePage
      section="Nonprofit website monitoring"
      eyebrow="For · Nonprofits and charities"
      title="Free Pro for registered nonprofits and charities."
      intro={`Your donation page and your website should stay up. Registered nonprofits and charities get Uptrack Pro free for ${GRANT_MONTHS} months. It renews each year while you still qualify.`}
      featuresHeading="What you get with free Pro"
      features={[
        ...PRO_FEATURES.map((name) => ({ title: name, desc: 'Same as the paid Pro plan. Nothing is held back.' })),
      ]}
      why={{
        heading: 'How you get it',
        body: `Sign in as an owner or admin of your organization. If a member has a confirmed email address on a domain that is on our lists, you claim Pro in Settings, Billing, and it starts at once. The lists are .ngo and .ong domains, the UK Charity Commission register, the Australian ACNC register, and our own list of nonprofits. If your domain is not listed, send an application with your registry number and a link to your public register entry. We check it and email you our decision. One organization holds free Pro for each domain.`,
      }}
      cta={{
        heading: `Pro is free for ${GRANT_MONTHS} months`,
        body: 'Start free, then claim the nonprofit grant in Settings, Billing. Before a grant ends, we email you 30 days and 7 days ahead and say what will pause. After it ends, the organization moves to the plan of its subscription, or to the Free plan if it has none.',
      }}
      visual={
        <ProductFrame url="uptrack.app/dashboard">
          <UptimeBarsPiece />
        </ProductFrame>
      }
    >
      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Who free Pro is not meant for</h2>
          <ul className="mt-3 max-w-xl list-disc space-y-1 pl-5 text-body">
            {NOT_ELIGIBLE.map((who) => (
              <li key={who}>{who}</li>
            ))}
          </ul>
        </div>
      </section>
      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Register data</h2>
          <p className="mt-3 max-w-xl text-body">
            We check domains against public registers. {REGISTER_CREDITS.uk}{' '}
            <a href={REGISTER_CREDITS.ukLicenceUrl} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
              Licence
            </a>
            . {REGISTER_CREDITS.acnc}{' '}
            <a href={REGISTER_CREDITS.acncLicenceUrl} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
              Licence
            </a>
            .
          </p>
        </div>
      </section>
    </FeaturePage>
  )
}
